import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Snackbar, Alert, ThemeProvider } from '@mui/material';
import axios from 'axios';

import { getTheme } from './theme';
import { empresas } from './config/empresas';
import LoginForm from './components/LoginForm';
import MainAppBar from './components/MainAppBar';
import PasswordModal from './components/PasswordModal';
import UserList from './components/UserList';
import BoletoList from './components/BoletoList';
import { API_BASE_URL } from './config';
import { passwordStrength } from './utils/passwordUtils';
import './App.css';

const authHeader = 'Basic Q2xvdWRTaGFyZWQ6Q2xvdWRTaGFyZWRAMTE2Mw==';

export default function App() {
  const [userData, setUserData] = useState(null);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [senhaDestaque, setSenhaDestaque] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [empresa, setEmpresa] = useState(process.env.REACT_APP_COMPANY || 'pontocerto');

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      const parsedData = JSON.parse(storedUserData);
      setUserData(parsedData);
      if (parsedData.empresa) {
        setEmpresa(parsedData.empresa);
      }
    }
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const limparDadosLogin = useCallback(() => {
    localStorage.removeItem('userData');
    setUserData(null);
  }, []);

  const handleLogin = async (usuarioRaw, senhaRaw) => {
    setErro('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/rota/boleto/usuario/validar`,
        { usuario: usuarioRaw, senha: senhaRaw },
        { headers: { Authorization: authHeader, 'Content-Type': 'application/json' } }
      );

      if (response.data?.acesso) {
        // Determina a empresa: 1) Do response, 2) Da variável de ambiente, 3) Do padrão
        let empresaId = response.data?.empresa || process.env.REACT_APP_COMPANY || 'pontocerto';
        setEmpresa(empresaId);

        const fullUserData = {
          ...response.data,
          senha: senhaRaw,
          empresa: empresaId,
        };
        setUserData(fullUserData);
        localStorage.setItem('userData', JSON.stringify(fullUserData));

        const seisDigitos = fullUserData.usuario.substring(0, 6);
        if (fullUserData.senha === seisDigitos) {
          showSnackbar('Por segurança, recomendamos que você altere sua senha.', 'warning');
          setSenhaDestaque(true);
        } else {
          showSnackbar('Login efetuado com sucesso!');
          setSenhaDestaque(false);
        }
      } else {
        setErro('Usuário ou senha inválidos');
        showSnackbar('Usuário ou senha inválidos.', 'error');
        limparDadosLogin();
      }
    } catch (err) {
      console.error(err);
      setErro('Erro ao autenticar. Tente novamente mais tarde.');
      showSnackbar('Erro ao autenticar usuário.', 'error');
      limparDadosLogin();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setNovaSenha('');
    setUserData(null);
    setErro('');
    localStorage.removeItem('userData');
    setOpenModal(false);
  };

  const handleSenhaUpdate = async () => {
    const strength = passwordStrength(novaSenha);
    if (strength < 5) {
      showSnackbar(
        'A senha deve ter 8+ caracteres, com maiúsculas, minúsculas, números e símbolos.',
        'warning'
      );
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/rota/boleto/usuario/atualizar`,
        { usuario: userData.usuario, senha: novaSenha },
        { headers: { Authorization: authHeader, 'Content-Type': 'application/json' } }
      );

      const updatedUserData = { ...userData, senha: novaSenha };
      setUserData(updatedUserData);
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
      setOpenModal(false);
      setNovaSenha('');
      showSnackbar('Senha atualizada com sucesso');
    } catch (err) {
      console.error(err);
      showSnackbar('Erro ao atualizar a senha.', 'error');
    }
  };

  const currentEmpresa = empresas[empresa];
  const theme = useMemo(
    () => getTheme(currentEmpresa.cores.primary, currentEmpresa.cores.secondary),
    [currentEmpresa]
  );

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <link rel="icon" type="image/x-icon" href={currentEmpresa.favicon.ico} />
        <link rel="icon" type="image/png" href={currentEmpresa.favicon.png} />
        <title>{currentEmpresa.nome} - Sistema de Boletos</title>
      </Helmet>

      {!userData ? (
        <Container
          maxWidth="sm"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 6,
            background: `linear-gradient(180deg, ${currentEmpresa.cores.secondary}20, ${currentEmpresa.cores.primary}05)`,
          }}
        >
          <LoginForm
            onSubmit={handleLogin}
            error={erro}
            loading={loading}
            empresa={currentEmpresa}
          />
        </Container>
      ) : (
        <>
          <MainAppBar
            empresa={currentEmpresa}
            userData={userData}
            senhaDestaque={senhaDestaque}
            setOpenModal={setOpenModal}
            handleLogout={handleLogout}
          />

          <Container
            sx={{
              mt: { xs: 10, sm: 12 },
              mb: 4,
              px: { xs: 2, sm: 3 },
            }}
          >
            {userData.role === 'master' ? (
              <UserList />
            ) : (
              <BoletoList
                usuario={userData.usuario}
                senha={userData.senha}
                authHeader={authHeader}
                senhaDestaque={senhaDestaque}
                empresa={currentEmpresa}
              />
            )}
          </Container>

          <PasswordModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            onUpdate={handleSenhaUpdate}
            novaSenha={novaSenha}
            setNovaSenha={setNovaSenha}
          />
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled">
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}